import React, { useState } from 'react';
import { Eye, EyeOff, RotateCcw, Info, AlertTriangle, CheckCircle2 } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · O3 · Rétros intérieur + extérieurs
// Œuvre pédagogique interactive — vue de dessus d'une voiture avec :
//  · 3 rétroviseurs (int + G + D) chacun avec un cône de vision colorable
//  · Toggle par rétro : ON/OFF (le cône disparaît)
//  · 4 véhicules autour (avant/derrière/à gauche/à droite) → cachés ou visibles
//  · Zones aveugles STRUCTURELLES (à traiter en O4) affichées en pointillés rouges
//  · Panneau : bilan de couverture visuelle + rôle de chaque rétro
// ══════════════════════════════════════════════════════════════════════════

const MIRRORS = [
  {
    id: 'int',
    label: 'Rétroviseur intérieur',
    color: '#0ea5e9',
    // Cône partant du rétroviseur (au dessus du pare-brise ~ x=400,y=290) vers l'arrière
    cone: 'M 400,290 L 220,600 L 580,600 Z',
    role: "Vue arrière DIRECTE, centrale, longue portée. Priorité n°1 : voir qui arrive derrière toi.",
    bonUsage: "Coup d'œil toutes les 10 s en circulation. AVANT chaque freinage. AVANT chaque changement de file.",
    role_hidden: "Zone arrière-centrale : véhicules qui approchent, distance de sécurité du suiveur.",
  },
  {
    id: 'gauche',
    label: 'Rétroviseur extérieur GAUCHE',
    color: '#8b5cf6',
    // Cône partant du rétro G (x=180 y=380) vers l'arrière gauche
    cone: 'M 180,380 L 30,600 L 220,600 Z',
    role: "Vue arrière GAUCHE, cadrage voie de gauche + partie de l'angle mort. En France (conduite à droite), c'est TON rétro le plus stratégique pour les dépassements.",
    bonUsage: "Coup d'œil AVANT tout dépassement, changement de file gauche, insertion sur une voie plus rapide.",
    role_hidden: "Zone arrière-gauche : véhicules dans la voie de gauche, motos en interfile.",
  },
  {
    id: 'droit',
    label: 'Rétroviseur extérieur DROIT',
    color: '#f59e0b',
    cone: 'M 620,380 L 570,600 L 780,600 Z',
    role: "Vue arrière DROITE, cadrage voie de droite + trottoir. Souvent oublié, pourtant vital en ville pour cyclistes et piétons.",
    bonUsage: "Coup d'œil AVANT tout virage à droite, avant d'ouvrir la portière côté trottoir, avant rabattement post-dépassement.",
    role_hidden: "Zone arrière-droite : cyclistes, piétons près du trottoir, véhicules qui te doublent par la droite (illégal mais fréquent).",
  },
];

// Véhicules positionnés autour, chacun est « couvert » par un ou plusieurs rétros
const VEHICLES = [
  {
    id: 'v-arriere',
    label: 'Voiture derrière',
    x: 400, y: 550, w: 50, h: 30,
    color: '#dc2626',
    coveredBy: ['int'],
    danger: "Sans rétro intérieur, tu ne sais pas si un véhicule te colle → freinage d'urgence = tôle froissée.",
  },
  {
    id: 'v-gauche',
    label: 'Moto arrière gauche',
    x: 130, y: 500, w: 28, h: 40,
    color: '#8b5cf6',
    coveredBy: ['gauche', 'int'],
    danger: "Sans rétro extérieur gauche, une moto qui remonte à ta gauche disparaît → collision au moment du changement de file.",
    isMoto: true,
  },
  {
    id: 'v-droit',
    label: 'Cycliste arrière droit',
    x: 680, y: 500, w: 22, h: 34,
    color: '#f59e0b',
    coveredBy: ['droit'],
    danger: "Sans rétro extérieur droit, un cycliste qui te longe à droite disparaît → percussion au moment du virage à droite.",
    isBike: true,
  },
  {
    id: 'v-avant',
    label: 'Voiture devant',
    x: 400, y: 150, w: 50, h: 30,
    color: '#64748b',
    coveredBy: [], // visible directement — non concerné par les rétros
    danger: "",
  },
];

export default function RetroTousSvg() {
  const [enabled, setEnabled] = useState({ int: true, gauche: true, droit: true });
  const [selected, setSelected] = useState(null); // id d'un rétro
  const [showBlind, setShowBlind] = useState(true);

  const toggle = (id) => setEnabled((e) => ({ ...e, [id]: !e[id] }));
  const reset = () => {
    setEnabled({ int: true, gauche: true, droit: true });
    setSelected(null);
  };

  const activeMirror = MIRRORS.find((m) => m.id === selected) || null;

  // Un véhicule est "vu" si au moins un rétro qui le couvre est activé
  const isVehicleSeen = (v) => v.coveredBy.length === 0 || v.coveredBy.some((c) => enabled[c]);

  const seenCount = VEHICLES.filter((v) => v.coveredBy.length > 0 && isVehicleSeen(v)).length;
  const totalCritical = VEHICLES.filter((v) => v.coveredBy.length > 0).length;

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {MIRRORS.map((m) => (
          <button
            key={m.id}
            onClick={() => { toggle(m.id); setSelected(m.id); }}
            data-testid={`o3-mirror-${m.id}`}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[11.5px] font-semibold ${
              enabled[m.id] ? 'text-white' : 'text-white/50 border-white/15'
            }`}
            style={enabled[m.id] ? { background: m.color + '22', borderColor: m.color + '80' } : {}}
          >
            {enabled[m.id] ? <Eye size={13} /> : <EyeOff size={13} />}
            {m.id === 'int' ? 'Intérieur' : m.id === 'gauche' ? 'Ext. Gauche' : 'Ext. Droit'}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setShowBlind((s) => !s)}
          data-testid="o3-toggle-blind"
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[11.5px] font-semibold ${
            showBlind ? 'bg-red-500/15 border-red-500/50 text-red-200' : 'border-white/15 text-white/60'
          }`}
        >
          Angles morts
        </button>
        <button
          type="button"
          onClick={reset}
          data-testid="o3-reset"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[11.5px] font-semibold ml-auto"
        >
          <RotateCcw size={13} /> Reset
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        {/* SVG vue de dessus */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Vue de dessus couverture rétroviseurs">
            <defs>
              <linearGradient id="o3-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0f172a" />
              </linearGradient>
              <linearGradient id="o3-car" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f5f5f5" />
                <stop offset="100%" stopColor="#94a3b8" />
              </linearGradient>
            </defs>

            {/* Route + trottoirs */}
            <rect x={0} y={0} width={800} height={620} fill="url(#o3-road)" />
            <rect x={0}   y={0} width={40}  height={620} fill="#475569" opacity={0.55} />
            <rect x={760} y={0} width={40}  height={620} fill="#475569" opacity={0.55} />
            {/* Lignes de voie */}
            <line x1={280} y1={0} x2={280} y2={620} stroke="#fbbf24" strokeWidth={2} strokeDasharray="18 12" opacity={0.6} />
            <line x1={520} y1={0} x2={520} y2={620} stroke="#fbbf24" strokeWidth={2} strokeDasharray="18 12" opacity={0.6} />

            {/* Cônes des rétros */}
            {MIRRORS.map((m) => (
              enabled[m.id] && (
                <g key={m.id}>
                  <path
                    d={m.cone}
                    fill={m.color}
                    fillOpacity={selected === m.id ? 0.28 : 0.15}
                    stroke={m.color}
                    strokeWidth={selected === m.id ? 2.5 : 1.5}
                    strokeDasharray={selected === m.id ? '' : '5 4'}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelected(m.id)}
                    data-testid={`o3-cone-${m.id}`}
                  />
                  <text
                    x={m.id === 'int' ? 400 : m.id === 'gauche' ? 120 : 700}
                    y={m.id === 'int' ? 480 : 540}
                    textAnchor="middle"
                    fill={m.color}
                    fontSize={10}
                    fontWeight="bold"
                    fontFamily="monospace"
                    opacity={0.9}
                    style={{ pointerEvents: 'none' }}
                  >
                    {m.id === 'int' ? 'CENTRAL' : m.id === 'gauche' ? 'GAUCHE' : 'DROIT'}
                  </text>
                </g>
              )
            ))}

            {/* Zones aveugles structurelles (piqûre O4) */}
            {showBlind && (
              <g opacity={0.85}>
                {/* Angle mort avant */}
                <polygon points="340,200 460,200 500,250 400,265 300,250" fill="#ef444425" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" />
                <text x={400} y={235} textAnchor="middle" fill="#ef4444" fontSize={9} fontWeight="bold">A.M. avant</text>
                {/* Angle mort côté gauche (sous rétro) */}
                <polygon points="200,395 340,395 340,485 220,485" fill="#ef444425" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" />
                <text x={270} y={445} textAnchor="middle" fill="#ef4444" fontSize={9} fontWeight="bold">A.M. gauche</text>
                {/* Angle mort côté droit */}
                <polygon points="460,395 600,395 580,485 460,485" fill="#ef444425" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" />
                <text x={530} y={445} textAnchor="middle" fill="#ef4444" fontSize={9} fontWeight="bold">A.M. droit</text>
              </g>
            )}

            {/* Voiture (vue de dessus, au centre) */}
            <g transform="translate(400 380)" filter="drop-shadow(0 4px 8px rgba(0,0,0,0.5))">
              {/* Carrosserie */}
              <rect x={-60} y={-95} width={120} height={190} rx={22} fill="url(#o3-car)" stroke="#0b1120" strokeWidth={3} />
              {/* Pare-brise avant */}
              <path d="M -50,-80 L 50,-80 L 42,-40 L -42,-40 Z" fill="#0ea5e9" opacity={0.5} />
              {/* Lunette arrière */}
              <path d="M -50,85 L 50,85 L 42,55 L -42,55 Z" fill="#0ea5e9" opacity={0.4} />
              {/* Toit */}
              <rect x={-42} y={-40} width={84} height={95} fill="#e2e8f0" />
              {/* Rétro intérieur (au centre haut du pare-brise) */}
              <rect x={-9} y={-90} width={18} height={6} fill={enabled.int ? MIRRORS[0].color : '#475569'} stroke="#0b1120" strokeWidth={1} />
              {/* Rétros extérieurs */}
              <rect x={-72} y={-30} width={14} height={8} rx={2} fill={enabled.gauche ? MIRRORS[1].color : '#475569'} stroke="#0b1120" strokeWidth={1} />
              <rect x={58} y={-30} width={14} height={8} rx={2} fill={enabled.droit ? MIRRORS[2].color : '#475569'} stroke="#0b1120" strokeWidth={1} />
              {/* Roues (vue dessus) */}
              <rect x={-64} y={-60} width={8} height={22} fill="#0b1120" />
              <rect x={56} y={-60} width={8} height={22} fill="#0b1120" />
              <rect x={-64} y={45} width={8} height={22} fill="#0b1120" />
              <rect x={56} y={45} width={8} height={22} fill="#0b1120" />
              {/* Label "TOI" */}
              <text x={0} y={0} textAnchor="middle" fill="#0b1120" fontSize={16} fontWeight="900" fontFamily="system-ui">TOI</text>
              <text x={0} y={16} textAnchor="middle" fill="#334155" fontSize={9} fontWeight="bold">conducteur</text>
            </g>

            {/* Véhicules alentours */}
            {VEHICLES.map((v) => {
              const seen = isVehicleSeen(v);
              return (
                <g key={v.id} opacity={seen ? 1 : 0.3}>
                  {v.isMoto ? (
                    <>
                      <rect x={v.x - v.w / 2} y={v.y - v.h / 2} width={v.w} height={v.h} rx={6} fill={v.color} stroke="#0b1120" strokeWidth={2} />
                      <circle cx={v.x} cy={v.y - v.h / 2 - 3} r={5} fill="#f5c9a2" stroke="#0b1120" strokeWidth={1} />
                    </>
                  ) : v.isBike ? (
                    <>
                      <ellipse cx={v.x} cy={v.y} rx={v.w / 2} ry={v.h / 2} fill={v.color} stroke="#0b1120" strokeWidth={2} />
                      <circle cx={v.x} cy={v.y - v.h / 2 - 4} r={4} fill="#f5c9a2" stroke="#0b1120" strokeWidth={1} />
                    </>
                  ) : (
                    <>
                      <rect x={v.x - v.w / 2} y={v.y - v.h / 2} width={v.w} height={v.h} rx={8} fill={v.color} stroke="#0b1120" strokeWidth={2} />
                      <rect x={v.x - v.w / 2 + 4} y={v.y - v.h / 2 + 4} width={v.w - 8} height={v.h - 8} rx={2} fill="#0b1120" opacity={0.5} />
                    </>
                  )}
                  {!seen && (
                    <text x={v.x} y={v.y + v.h / 2 + 15} textAnchor="middle" fill="#ef4444" fontSize={10} fontWeight="bold" fontFamily="monospace">
                      INVISIBLE
                    </text>
                  )}
                  {seen && v.coveredBy.length > 0 && (
                    <circle cx={v.x + v.w / 2 + 6} cy={v.y - v.h / 2 + 4} r={5} fill="#22c55e" stroke="#0b1120" strokeWidth={1.5} />
                  )}
                </g>
              );
            })}

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.6} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">O3 · Couverture des 3 rétroviseurs</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C1h — Vision arrière + latérale complète</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>

          {/* Compteur de couverture */}
          <div className="p-3.5 border-t border-white/10 bg-black/40 flex items-center justify-between gap-3">
            <div>
              <div className="text-white/50 text-[10px] font-mono-spec uppercase tracking-wider font-bold">
                Véhicules couverts par tes rétros actifs
              </div>
              <div className="mt-0.5 font-serif-editorial text-white text-[18px] leading-none">
                {seenCount}<span className="text-white/40 text-[13px]"> / {totalCritical}</span>
              </div>
            </div>
            <div className="flex-1 max-w-[200px]">
              <div className="w-full h-2.5 rounded-full bg-black/60 border border-white/10 overflow-hidden">
                <div
                  className="h-full transition-all duration-300"
                  style={{
                    width: `${(seenCount / totalCritical) * 100}%`,
                    background: seenCount === totalCritical ? '#22c55e' : seenCount === 0 ? '#dc2626' : '#f59e0b',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="o3-panel">
          {activeMirror ? (
            <>
              <div className="rounded-xl border-2 p-3.5" style={{ borderColor: activeMirror.color, background: activeMirror.color + '15' }}>
                <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: activeMirror.color }}>
                  Rétroviseur sélectionné
                </div>
                <div className="mt-0.5 font-serif-editorial text-white text-[18px] leading-tight">
                  {activeMirror.label}
                </div>
                <div className="mt-1 text-white/60 text-[11px] font-mono-spec">
                  {enabled[activeMirror.id] ? '● Activé' : '○ Désactivé'}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
                <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
                <div>
                  <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Rôle</div>
                  <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{activeMirror.role}</p>
                </div>
              </div>

              <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
                <div>
                  <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Bon usage</div>
                  <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{activeMirror.bonUsage}</p>
                </div>
              </div>

              {!enabled[activeMirror.id] && (
                <div className="mt-3 rounded-lg border border-red-500/50 bg-red-950/25 p-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-red-300 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Sans ce rétro</div>
                    <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{activeMirror.role_hidden}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
                <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                  Explorer chaque rétroviseur
                </div>
                <p className="mt-1.5 text-white/80 text-[12.5px] leading-relaxed">
                  Clique sur un rétroviseur (haut) pour voir son cône de vision, ou clique directement sur un cône dans le schéma pour ouvrir sa fiche.
                </p>
              </div>

              <div className="mt-4 space-y-2">
                {MIRRORS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSelected(m.id)}
                    className="w-full text-left rounded-lg border p-2.5 hover:bg-white/[0.03]"
                    style={{ borderColor: m.color + '40', background: m.color + '08' }}
                  >
                    <div className="text-[10px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: m.color }}>
                      {m.id === 'int' ? 'Intérieur' : m.id === 'gauche' ? 'Ext. Gauche' : 'Ext. Droit'}
                    </div>
                    <div className="text-white text-[12.5px] mt-0.5 leading-tight">{m.label}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-950/15 p-2.5 text-[11.5px] text-amber-100/90 leading-relaxed">
            <span className="text-amber-300 font-mono-spec text-[10px] uppercase tracking-wider font-bold block mb-0.5">Rappel</span>
            Même avec les 3 rétros, il reste 3 zones invisibles (angles morts). Passe à <span className="text-amber-200 font-semibold">O4</span> pour compléter la couverture par le contrôle-épaule.
          </div>
        </div>
      </div>
    </div>
  );
}
